import {
  ActiveFilters,
  Alert,
  Button,
  Card,
  CardBody,
  DateRangePicker,
  dateRangePresets,
  EmptyState,
  formatDate,
  FormField,
  Highlight,
  Money,
  Pagination,
  SearchInput,
  SearchSelect,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableOnly,
  TableRow,
  TableTotalRow,
  Text,
  Toolbar,
  ToolbarCount,
  ToolbarItem,
  ToolbarSpacer,
  type ActiveFilter,
} from '@hkdevs/design-system';
import { Fragment, useRef, useState } from 'react';
import {
  useCustomers,
  useLineItems,
  useProductTypes,
  type SearchLineItem,
} from '../../api/queries/search';
import { useTeamReps } from '../../api/queries/team';
import type { components } from '../../api/schema.gen';
import { formatValue } from '../lineItems/LineItemList';
import { formatRange, toDate, toIso } from '../payHistory/PayHistoryView';

export const PAGE_SIZE = 25;

/** A row's configured columns that have a value, in the table's order (ADR 0030). */
const filled = (
  item: SearchLineItem,
  columns: readonly components['schemas']['LineItemColumn'][],
) => columns.filter((column) => !column.fixed && item.values?.[column.key]?.trim());

/** Whether the search text is in a note, as the API's ilike matches it. */
export const noteMatches = (note: string, q: string | undefined) =>
  !!q?.trim() && note.toLowerCase().includes(q.trim().toLowerCase());

/** Pay dates' one-click ranges, from today. A preset writes concrete from/to to the URL,
 * so a shared link keeps the same dates (A16). */
const DATE_PRESETS = [
  dateRangePresets.lastDays(30),
  dateRangePresets.lastDays(90),
  dateRangePresets.thisYear(),
  dateRangePresets.lastYear(),
];

type Labels = components['schemas']['FixedColumnLabels'];

/** The fixed columns' labels every tenant starts with, until the results bring the
 * tenant's (Settings → Line item table). */
export const DEFAULT_LABELS: Labels = {
  customer: 'Customer',
  productType: 'Product Type',
  amount: 'Amount Earned',
};

/** The search's URL state (ADR 0023): every filter and the page. */
export interface LineItemSearchParams {
  /** Pay dates, inclusive, ISO; either end may be open. */
  from?: string | undefined;
  to?: string | undefined;
  /** An exact customer name from the typeahead. */
  customer?: string | undefined;
  productType?: string | undefined;
  /** Free text: customer, note or product type. */
  q?: string | undefined;
  /** A supervisor's rep filter: a rep's ID; left out for all their reps. */
  rep?: string | undefined;
  /** 1-based; left out for the first. */
  page?: number | undefined;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseLineItemSearch(search: Record<string, unknown>): LineItemSearchParams {
  const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  const date = (value: unknown) => {
    const iso = text(value);
    return iso && ISO_DATE.test(iso) ? iso : undefined;
  };
  const rep = text(search.rep);
  const page = Number(search.page);
  return {
    from: date(search.from),
    to: date(search.to),
    customer: text(search.customer),
    productType: text(search.productType),
    q: text(search.q),
    rep: rep && UUID.test(rep) ? rep : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
  };
}

type FilterId = 'range' | 'customer' | 'productType' | 'rep' | 'q';

/** "07/01/2026 – 07/31/2026", "From 07/01/2026" or "To 07/31/2026". */
function rangeLabel(from?: string, to?: string) {
  if (from && to) return formatRange(from, to);
  if (from) return `From ${formatDate(from)}`;
  return to ? `To ${formatDate(to)}` : '';
}

/** The filters in force, as chips. */
export function activeFilters(
  search: LineItemSearchParams,
  repName: (id: string) => string,
  labels: Labels = DEFAULT_LABELS,
): ActiveFilter[] {
  const chips: ActiveFilter[] = [];
  if (search.from || search.to) {
    chips.push({ id: 'range', label: `Pay dates: ${rangeLabel(search.from, search.to)}` });
  }
  if (search.customer) {
    chips.push({ id: 'customer', label: `${labels.customer}: ${search.customer}` });
  }
  if (search.productType) {
    chips.push({ id: 'productType', label: `${labels.productType}: ${search.productType}` });
  }
  if (search.rep) chips.push({ id: 'rep', label: `Rep: ${repName(search.rep)}` });
  if (search.q?.trim()) chips.push({ id: 'q', label: `Text: ${search.q.trim()}` });
  return chips;
}

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
const dollars = (cents: number) => (cents / 100).toFixed(2);

/**
 * Whose line items a search shows (a rep's, the caller's own, or a supervisor's team or
 * one of their reps) and the filters, as the list and Export PDF both send them.
 */
export function searchScope(
  repId: string | undefined,
  supervisor: boolean,
  search: LineItemSearchParams,
) {
  const pickRep = supervisor && !repId;
  const scope = repId ?? (pickRep ? (search.rep ?? 'all') : undefined);
  return {
    pickRep,
    scope,
    filters: {
      repId: scope,
      from: search.from,
      to: search.to,
      customer: search.customer,
      productType: search.productType,
      q: search.q?.trim() || undefined,
    },
  };
}

export interface LineItemSearchViewProps {
  /** One rep's line items (/reps/$repId/search); otherwise the caller's own, or a team. */
  repId?: string;
  /** A supervisor on their own Search: a rep filter, and all their reps by default. */
  supervisor?: boolean;
  search: LineItemSearchParams;
  onSearchChange: (next: LineItemSearchParams) => void;
}

/** Line-item search (pc-dbb.17) across every pay date the caller may see. */
export function LineItemSearchView({
  repId,
  supervisor = false,
  search,
  onSearchChange,
}: LineItemSearchViewProps) {
  const searchRef = useRef<HTMLDivElement>(null);
  const [customerInput, setCustomerInput] = useState('');
  const { pickRep, scope, filters } = searchScope(repId, supervisor, search);
  const page = search.page ?? 1;
  const results = useLineItems({ ...filters, page, pageSize: PAGE_SIZE });
  const customers = useCustomers(scope, customerInput.trim());
  const productTypes = useProductTypes(scope);
  const team = useTeamReps('history');
  const reps = pickRep ? (team.data ?? []) : undefined;
  const repName = (id: string) => reps?.find((rep) => rep.id === id)?.name ?? 'One rep';

  /** New filters go back to the first page. */
  const set = (patch: Partial<LineItemSearchParams>) =>
    onSearchChange({ ...search, ...patch, page: undefined });
  const labels = results.data?.labels ?? DEFAULT_LABELS;
  const customerLabel = labels.customer.toLowerCase();
  const productTypeLabel = labels.productType.toLowerCase();
  const chips = activeFilters(search, repName, labels);
  const clearAll = () => onSearchChange({});
  const clear = (id: string) => {
    const key = id as FilterId;
    set(key === 'range' ? { from: undefined, to: undefined } : { [key]: undefined });
  };
  const columns = pickRep ? 5 : 4;

  let body;
  if (results.isError) {
    body = (
      <Alert
        tone="danger"
        title="Line items couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void results.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  } else if (results.data && results.data.total === 0) {
    body = (
      <Card>
        <CardBody>
          {chips.length > 0 ? (
            <EmptyState
              title="No line items match"
              description={`Try a different ${customerLabel}, ${productTypeLabel} or date range, or clear the filters.`}
              icon="search"
              headingLevel={2}
              actions={
                <Button size="sm" variant="secondary" onClick={clearAll}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No line items yet"
              description="Line items appear here once your pay is processed."
              icon="file"
              headingLevel={2}
            />
          )}
        </CardBody>
      </Card>
    );
  } else {
    const data = results.data;
    const pageCount = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    body = (
      <>
        <Card flush>
          <Table aria-label="Line items" responsive="cards" busy={!data || results.isFetching}>
            <TableHead>
              <TableCell>Pay date</TableCell>
              <TableCell>{labels.customer}</TableCell>
              <TableCell>{labels.productType}</TableCell>
              {pickRep && <TableCell>Rep</TableCell>}
              <TableCell numeric>{labels.amount}</TableCell>
            </TableHead>
            <TableBody>
              {!data ? (
                <TableLoading colSpan={columns} rows={6} label="Loading line items" />
              ) : (
                <>
                  {data.items.map((item) => {
                    const details = filled(item, data.columns);
                    const noteMatched = !!item.note && noteMatches(item.note, search.q);
                    return (
                      <TableRow key={item.id}>
                        <TableCell label="Pay date">{formatDate(item.payDate)}</TableCell>
                        {/* Under the customer (A16): the configured columns ("Label value ·
                            Label value"; in cards, their own rows below), then a note that
                            matched; the search text marked. */}
                        <TableCell
                          rowHeader
                          label={labels.customer}
                          stacked={details.length > 0 || noteMatched}
                        >
                          <Highlight text={item.customer ?? item.productType} query={search.q} />
                          {details.length > 0 && (
                            <TableOnly>
                              <Text as="div" tone="muted" size="meta">
                                {details.map((column, index) => (
                                  <Fragment key={column.key}>
                                    {index > 0 && ' · '}
                                    {column.label}{' '}
                                    {formatValue(item.values?.[column.key], column.format)}
                                  </Fragment>
                                ))}
                              </Text>
                            </TableOnly>
                          )}
                          {noteMatched && (
                            <Text as="div" tone="muted" size="meta">
                              <Highlight text={item.note!} query={search.q} excerpt label="Note:" />
                            </Text>
                          )}
                        </TableCell>
                        <TableCell label={labels.productType} tone="muted">
                          <Highlight text={item.productType} query={search.q} />
                        </TableCell>
                        {pickRep && <TableCell label="Rep">{item.rep?.name}</TableCell>}
                        {details.map((column) => (
                          <TableCell key={column.key} label={column.label} tone="muted" cardsOnly>
                            {formatValue(item.values?.[column.key], column.format)}
                          </TableCell>
                        ))}
                        <TableCell label={labels.amount} numeric>
                          <Money value={dollars(item.amountCents)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableTotalRow>
                    <TableCell rowHeader colSpan={columns - 1}>
                      {`Total, ${plural(data.total, 'line item', 'line items')}`}
                    </TableCell>
                    <TableCell numeric>
                      <Money value={dollars(data.totalAmountCents)} />
                    </TableCell>
                  </TableTotalRow>
                </>
              )}
            </TableBody>
          </Table>
        </Card>
        {data && pageCount > 1 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={(next) =>
              onSearchChange({ ...search, page: next > 1 ? next : undefined })
            }
            size="sm"
          />
        )}
      </>
    );
  }

  return (
    <Stack gap={3} shrink>
      {/* The text search on its own full-width row, so its placeholder is never cut off,
          with a hint saying what it matches. The page is Search, so no visible label. */}
      <div ref={searchRef}>
        <FormField hint={`Matches ${customerLabel}, ${productTypeLabel} or note`}>
          <SearchInput
            aria-label="Search line items"
            placeholder="Search line items"
            value={search.q ?? ''}
            onValueChange={(q) => set({ q: q || undefined })}
          />
        </FormField>
      </div>
      <Toolbar aria-label="Filter line items" align="end">
        <ToolbarItem size="range">
          <FormField label="Pay dates">
            <DateRangePicker
              clearable
              allowTextInput
              presets={DATE_PRESETS}
              value={{
                start: search.from ? toDate(search.from) : null,
                end: search.to ? toDate(search.to) : null,
              }}
              onChange={({ start, end }) =>
                set({ from: start ? toIso(start) : undefined, to: end ? toIso(end) : undefined })
              }
            />
          </FormField>
        </ToolbarItem>
        <ToolbarItem size="filter">
          <FormField label={labels.customer}>
            <SearchSelect
              placeholder={`Any ${customerLabel}`}
              options={(customers.data ?? []).map((name) => ({ value: name, label: name }))}
              filter={false}
              loading={customers.isFetching}
              emptyMessage="No matches"
              value={search.customer ?? null}
              onChange={(customer) => set({ customer: customer ?? undefined })}
              onInputChange={setCustomerInput}
              onClear={() => set({ customer: undefined })}
            />
          </FormField>
        </ToolbarItem>
        <ToolbarItem size="filter">
          <FormField label={labels.productType}>
            <Select
              options={[
                { value: '', label: `Any ${productTypeLabel}` },
                ...(productTypes.data ?? []).map((type) => ({ value: type, label: type })),
              ]}
              value={search.productType ?? ''}
              onChange={(event) => set({ productType: event.target.value || undefined })}
            />
          </FormField>
        </ToolbarItem>
        {reps && (
          <ToolbarItem size="filter">
            <FormField label="Rep">
              <SearchSelect
                placeholder="All my reps"
                options={[
                  { value: 'all', label: 'All my reps' },
                  ...reps.map((rep) => ({ value: rep.id, label: rep.name })),
                ]}
                value={search.rep ?? 'all'}
                onChange={(rep) => set({ rep: rep && rep !== 'all' ? rep : undefined })}
                {...(search.rep && { onClear: () => set({ rep: undefined }) })}
              />
            </FormField>
          </ToolbarItem>
        )}
        <ToolbarSpacer />
        {results.data && (
          <ToolbarCount>{plural(results.data.total, 'line item', 'line items')}</ToolbarCount>
        )}
      </Toolbar>
      <ActiveFilters
        filters={chips}
        onRemove={clear}
        onClearAll={clearAll}
        emptyFocusTarget={() => searchRef.current?.querySelector('input')}
      />
      {body}
    </Stack>
  );
}
